import { useCallback, useEffect, useState } from "react";
import { foyerApi } from "../http/foyer";
import type { PremiseConfig } from "@shared/types/foyer";

export interface PremiseState {
  config: PremiseConfig | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  /** Adopt a config the server has already confirmed (a PUT response) as the
   *  saved copy, without a redundant refetch. A caller that writes the premise
   *  must call this with the response, or this hook keeps handing out the
   *  pre-save copy (and its old `version`, which the next write would send). */
  applySaved: (next: PremiseConfig) => void;
}

export function usePremise(premiseId: string): PremiseState {
  const [config, setConfig] = useState<PremiseConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    // No id, no request. Reachable whenever a product page is open with a
    // customer scoped but no location - chain-level signage authoring, for
    // one - where the old code fetched `/api/foyer/premise/` and took a 403
    // on every render. Harmless to the page, but it logged a console error
    // that looked like a permissions bug and cost a round trip.
    if (!premiseId) {
      setConfig(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setConfig(await foyerApi.getPremise(premiseId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [premiseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applySaved = useCallback((next: PremiseConfig) => {
    setConfig(next);
  }, []);

  return { config, loading, error, refresh, applySaved };
}
